import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Paper,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HtmlIcon from '@mui/icons-material/Html';
import {
  buildPortfolioReport,
  downloadPortfolioHtml,
  downloadPortfolioPdf,
  PORTFOLIO_DECISION_LABELS,
} from '../../utils/portfolioReportUtils';
import { formatTry } from '../../utils/reportRevenueUtils';
import showNotification from '../common/Notification';

/**
 * Portfolio report generator — brand + date filters, HTML/PDF export
 */
const PortfolioReport = ({ open, onClose, brands = [], croKpis }) => {
  const previewRef = useRef(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);

  const brandOptions = useMemo(
    () => brands.map((b) => ({ id: String(b._id), name: b.name })),
    [brands],
  );

  const report = useMemo(
    () =>
      buildPortfolioReport(brands, {
        brandIds: selectedBrands.map((b) => b.id),
        dateFrom,
        dateTo,
      }),
    [brands, selectedBrands, dateFrom, dateTo],
  );

  const handleExportHtml = useCallback(() => {
    try {
      downloadPortfolioHtml(report);
      showNotification('HTML raporu indirildi', 'success');
    } catch {
      showNotification('HTML dışa aktarılamadı', 'error');
    }
  }, [report]);

  const handleExportPdf = useCallback(async () => {
    if (!previewRef.current) return;

    try {
      setExportingPdf(true);
      await downloadPortfolioPdf(previewRef.current);
      showNotification('PDF raporu indirildi', 'success');
    } catch {
      showNotification('PDF dışa aktarılamadı', 'error');
    } finally {
      setExportingPdf(false);
    }
  }, []);

  const { summary, winners, decisionCounts } = report;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#240B4B' }}>
        Portföy Raporu Oluştur
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={brandOptions}
              getOptionLabel={(opt) => opt.name}
              value={selectedBrands}
              onChange={(_, value) => setSelectedBrands(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Markalar" placeholder="Tüm markalar" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Başlangıç tarihi"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Bitiş tarihi"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        {/* Preview area — captured for PDF export */}
        <Paper
          ref={previewRef}
          elevation={0}
          sx={{ p: 3, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fff' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#240B4B', mb: 0.5 }}>
            HypeCRO Portföy Özeti
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {report.brandNames.join(', ') || 'Tüm markalar'}
            {(dateFrom || dateTo) && ` · ${dateFrom || '…'} — ${dateTo || '…'}`}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Toplam Test', value: summary.totalTests },
              { label: 'Kazanan', value: summary.winnerCount },
              { label: 'Ort. Uplift', value: `+${summary.avgUplift}%` },
              { label: 'Gelir / Yıl', value: formatTry(summary.totalRevenue) },
            ].map((kpi) => (
              <Grid item xs={6} sm={3} key={kpi.label}>
                <Box sx={{ bgcolor: '#f7f9fc', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#240B4B' }}>
                    {kpi.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kpi.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {croKpis && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Portföy win rate: %{croKpis.winRate} · Test hızı (30g): {croKpis.testVelocity?.created ?? 0} yeni
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Karar Dağılımı
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {Object.entries(decisionCounts).map(([key, count]) => (
              <Chip
                key={key}
                label={`${PORTFOLIO_DECISION_LABELS[key] || key}: ${count}`}
                size="small"
                variant="outlined"
              />
            ))}
            {Object.keys(decisionCounts).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Karar verisi yok
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Kazanan Testler ({winners.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Marka</TableCell>
                <TableCell>Test</TableCell>
                <TableCell align="right">Uplift</TableCell>
                <TableCell>Karar</TableCell>
                <TableCell align="right">Gelir / Yıl</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {winners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Seçilen dönemde kazanan test yok
                  </TableCell>
                </TableRow>
              ) : (
                winners.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.brandName}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell align="right">+{t.maxImprovement ?? '—'}%</TableCell>
                    <TableCell>{PORTFOLIO_DECISION_LABELS[t.decision] || '—'}</TableCell>
                    <TableCell align="right">
                      {t.estimatedYearlyRevenue ? formatTry(t.estimatedYearlyRevenue) : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Kapat</Button>
        <Button startIcon={<HtmlIcon />} onClick={handleExportHtml} variant="outlined">
          HTML İndir
        </Button>
        <Button
          startIcon={exportingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          onClick={handleExportPdf}
          variant="contained"
          disabled={exportingPdf}
          sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
        >
          PDF İndir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortfolioReport;
